import React from 'react';
import _ from 'lodash';
import connectFirebase from '../lib/firebase/connectFirebase';
import { Column, Expanded, Row } from '../styled/flex';
import { ConnectedChatList } from './ChatList';
import './ChatRoom.css';
import { BrowserRouter as Router, Route, Link } from 'react-router-dom';
import ReactLoading from 'react-loading';
import { interval } from 'rxjs';
import { filter } from 'rxjs/operators';
import firebase from 'firebase';
import uuid4 from 'uuid/v4';
import ChatRoomStyle from './ChatRoomStyle';

const TARGET_LANGUAGES = [
  { code: 'en', flag: '🇬🇧' },
  { code: 'it', flag: '🇮🇹' },
  { code: 'es', flag: '🇪🇸' },
  { code: 'fr', flag: '🇫🇷' },
  { code: 'de', flag: '🇩🇪' },
  { code: 'ja', flag: '🇯🇵' },
];

const TARGET_LANGUAGE_CODES = TARGET_LANGUAGES.map(({ code }) => code);
const TRANSLATION_ALLOWED_EMAILS = [
  'jun.hong@bepro11.com',
  'kang@bepro11.com',
];

const canUseTranslation = () => {
  const email = (firebase.auth().currentUser?.email || '').toLowerCase();
  return TRANSLATION_ALLOWED_EMAILS.includes(email);
};

const languageFlag = (code) => {
  const language = _.find(TARGET_LANGUAGES, { code });
  return language ? language.flag : code;
};

const normalizeLanguage = (value) => {
  if (!value || typeof value !== 'string') return null;
  const code = value.toLowerCase().replace('_', '-').split('-')[0];
  if (!code) return null;
  return code === 'jp' ? 'ja' : code;
};

class PendingMessage {
  constructor({ body, userId, imageUrl = null, translation = null }) {
    const createdDate = new Date();
    this.id = uuid4();
    this.body = body;
    this.userId = userId;
    this.imageUrl = imageUrl;
    this.translation = translation;
    this.type = 'admin';
    this.created = {
      toDate: () => createdDate,
    };
    this.adminUserEmail = firebase.auth().currentUser.email;
    this.isPendingMessage = true;
  }

  toServerData = () => {
    return {
      body: this.body,
      userId: this.userId,
      imageUrl: this.imageUrl,
      type: this.type,
      created: this.created.toDate(),
      adminUserEmail: this.adminUserEmail,
    };
  };
}

export class ChatRoom extends React.PureComponent {
  state = {
    body: '',
    imageUrl: null,
    uploadingImage: false,
    pendingMessages: {},
    translationPreview: null,
    translatingPreview: false,
    translatingMessageId: null,
    targetLanguage: null,
  };

  _onBeforeUnload = (e) => {
    if (!_.isEmpty(this.state.pendingMessages)) {
      e.preventDefault();

      e.returnValue = '';
    }
  };

  get lastUserMessageLanguage() {
    const { messages } = this.props;
    const lastMessage = _.maxBy(_.filter(messages, { type: 'user' }), (m) =>
      m.created.toDate().getTime()
    );

    if (!lastMessage) return null;
    return lastMessage.language;
  }

  get lastDetectedSourceLanguage() {
    const { messages } = this.props;
    const lastDetected = _.maxBy(
      _.filter(
        messages,
        (m) => m.type === 'user' && m.sourceLanguage
      ),
      (m) => m.created.toDate().getTime()
    );
    const language = normalizeLanguage(
      lastDetected && lastDetected.sourceLanguage
    );
    return TARGET_LANGUAGE_CODES.includes(language) ? language : null;
  }

  get selectedTargetLanguage() {
    return (
      this.state.targetLanguage ||
      this.lastDetectedSourceLanguage ||
      'en'
    );
  }

  UNSAFE_componentWillMount() {
    const { sendMessage } = this.props;

    window.addEventListener('beforeunload', this._onBeforeUnload);

    this._unsubscribe = interval(33)
      .pipe(filter(() => !_.isEmpty(this.state.pendingMessages)))
      .subscribe(() => {
        const now = Date.now();
        const sendTargetMessages = _.filter(this.state.pendingMessages, (m) => {
          // 3초 후 실제 전송
          return m.created.toDate().getTime() < now - 5000;
        });

        const reminingMessages = _.omit(this.state.pendingMessages, [
          ..._.map(sendTargetMessages, 'id'),
        ]);

        this.setState(
          {
            pendingMessages: reminingMessages,
          },
          () => {
            _.forEach(sendTargetMessages, (m) => {
              sendMessage(m.body, {
                imageUrl: m.imageUrl,
                language:
                  this.lastDetectedSourceLanguage ||
                  this.lastUserMessageLanguage,
                translation: m.translation || null,
              });
            });
          }
        );
      });
  }

  UNSAFE_componentWillUnmount() {
    window.removeEventListener('beforeunload', this._onBeforeUnload);
    _.result(this, '_unsubscribe');
  }

  _onFileSelect = async (e) => {
    if (_.isEmpty(e.target.files)) return;

    const { uploadImage } = this.props;

    const file = _.first(e.target.files);
    this.setState({ uploadingImage: true });
    const url = await uploadImage(file);
    console.log('url', url);
    this.setState({ imageUrl: url, uploadingImage: false });
  };

  _onPaste = async (e) => {
    const { uploadImage } = this.props;
    const item = _.first(e.clipboardData.items);
    if (!item) return;
    if (item.type.indexOf('image') < 0) return;

    this.setState({ uploadingImage: true });
    const url = await uploadImage(item.getAsFile());
    this.setState({ imageUrl: url, uploadingImage: false });
  };

  _onSubmit = (e) => {
    const { userId } = this.props;
    const { imageUrl, body, translationPreview } = this.state;
    e.preventDefault();
    if (!body && !imageUrl) return;

    const pendingMessage = new PendingMessage({
      body: translationPreview || body,
      translation: translationPreview ? body : null,
      imageUrl,
      userId,
    });

    this.setState({
      body: '',
      imageUrl: null,
      translationPreview: null,
      pendingMessages: {
        ...this.state.pendingMessages,
        [pendingMessage.id]: pendingMessage,
      },
    });
  };

  _onTranslateMessage = async (message) => {
    const { translateText, saveTranslation } = this.props;
    if (!message.body || this.state.translatingMessageId) return;

    this.setState({ translatingMessageId: message.id });
    try {
      const { translation, sourceLanguage } = await translateText(
        message.body,
        'en',
        'inbound'
      );
      await saveTranslation(message.id, translation, sourceLanguage);
      const nextState = { translatingMessageId: null };
      if (TARGET_LANGUAGE_CODES.includes(sourceLanguage)) {
        nextState.targetLanguage = sourceLanguage;
      }
      this.setState(nextState);
    } catch (error) {
      this.setState({ translatingMessageId: null });
      window.alert(error.message || 'Translation failed');
    }
  };

  _onTranslatePreview = async () => {
    const { translateText } = this.props;
    const { body, translatingPreview } = this.state;
    const targetLanguage = this.selectedTargetLanguage;
    if (!body.trim() || !targetLanguage || translatingPreview) {
      return;
    }

    this.setState({ translatingPreview: true });
    try {
      const { translation } = await translateText(
        body,
        targetLanguage,
        'outbound'
      );
      this.setState({ translationPreview: translation, translatingPreview: false });
    } catch (error) {
      this.setState({ translatingPreview: false });
      window.alert(error.message || 'Translation failed');
    }
  };

  _onSelectTargetLanguage = (language) => {
    this.setState({
      targetLanguage: language,
      translationPreview: null,
    });
  };

  _onDismissPreview = () => {
    this.setState({ translationPreview: null });
  };

  render() {
    const { chatroom, messages, sendMessage, deleteMessage } = this.props;
    const translationEnabled = canUseTranslation();
    const {
      imageUrl,
      uploadingImage,
      body,
      pendingMessages,
      translationPreview,
      translatingPreview,
      translatingMessageId,
    } = this.state;
    const selectedTargetLanguage = this.selectedTargetLanguage;
    const canTranslatePreview =
      !!body.trim() && !translatingPreview && !translationPreview;
    const currentUserEmail = firebase.auth()?.currentUser?.email;
    
    const subscribers = _.get(chatroom, 'subscribers', []);
    const isSubscribed = subscribers.includes(currentUserEmail);
    console.log('isSubscribed', subscribers, currentUserEmail, isSubscribed);

    const orderedMessages = _.orderBy(
      { ...messages, ...pendingMessages },
      (m) => m.created.toDate(),
      'desc'
    );

    return (
      <ChatRoomStyle style={{ height: '100%' }}>
        <Column className="chat-room" style={{ height: '100%' }}>
          <Row className={'chat-room-header'}>
            <Link to="/">
              <i className="icon bepro-arrow-b-left" />
            </Link>
            <div className="chatroom-name">
              {_.get(chatroom, 'name', '')} ({_.get(chatroom, 'id', '')})
            </div>
            <button className={`slack-alert-button ${isSubscribed ? 'active' : ''}`} 
            onClick={() => this.props.toggleSubscription(!isSubscribed)}
            >
              <i className={`icon ${isSubscribed ? 'bepro-bell-slash' : 'bepro-bell'}`} style={{ fontSize: 18}} />
              {isSubscribed ? 'Subscribed' : 'Tag me in Slack'}
            </button>
          </Row>

          <Column
            className={'chat-room-body'}
            reverse
            style={{ flex: 1, flexGrow: 1, overflowY: 'auto' }}
          >
            {_.map(orderedMessages, (m, i) => (
              <div className={`message-item ${m.type}`} key={m.id + '-bubble'}>
                {_.compact([
                  ..._.map(m.attachments, (a, aIndex) => {
                    const { title, text, fields } = a;

                    return (
                      <div
                        key={m.id + '-attachment-' + aIndex}
                        className={`attachment ${
                          m.type === 'admin' ? 'right' : 'left'
                        }`}
                        style={{
                          alignSelf:
                            m.type === 'admin' ? 'flex-end' : 'flex-start',
                        }}
                      >
                        {!!title && (
                          <div className="attachment-title">{title}</div>
                        )}
                        {!!text && (
                          <div className="attachment-text">{text}</div>
                        )}
                        {_.map(fields, (f, fIndex) => {
                          const { title, value } = f;

                          return (
                            <div
                              className="attachment-field"
                              key={
                                m.id +
                                '-attachment-' +
                                aIndex +
                                '-field-' +
                                fIndex
                              }
                            >
                              <div className="field-title">{title}</div>
                              <div className="field-value">{value}</div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }),
                  <div
                    className={`message-wrapper ${
                      m.isPendingMessage ? 'pending' : ''
                    }`}
                  >
                    <div
                      className={`message-time ${m.type}`}
                      key={m.id + '-time'}
                    >
                      {m.isPendingMessage ? (<span className={'blue'}>Sending</span>) : (
                        m.adminUserEmail ? (
                          <div className={'admin-info'}>
                            <div className={'tooltip'}>Only visible to admins</div>
                            <span className={'blue'}>{m.adminUserEmail.split("@")[0]}</span>
                          </div>
                        ) : ""
                      )}
                      <span>{m.created.toDate().toLocaleDateString()}</span>
                      <span>
                        {m.created
                          .toDate()
                          .toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                      </span>
                    </div>
                    <div
                      className={`message-stack ${m.type}`}
                      style={{
                        alignSelf:
                          m.type === 'admin' ? 'flex-end' : 'flex-start',
                      }}
                      key={m.id}
                    >
                      <div className={`message ${m.type}`}>
                        {!!m.imageUrl && (
                          <img
                            style={{
                              maxWidth: '100%',
                            }}
                            src={m.imageUrl}
                            alt=""
                          />
                        )}
                        {m.body || ''}
                        {translationEnabled &&
                          m.type === 'user' &&
                          !!m.body &&
                          !m.translation && (
                            <button
                              type="button"
                              className="btn-translate-message"
                              disabled={translatingMessageId === m.id}
                              onClick={() => this._onTranslateMessage(m)}
                            >
                              {translatingMessageId === m.id
                                ? 'Translating…'
                                : 'Translate'}
                            </button>
                          )}
                      </div>
                      {translationEnabled && !!m.translation && (
                        <div className="message-translation">
                          {m.translation}
                        </div>
                      )}
                    </div>

                    {m.type === 'admin' ? (
                      <button
                        className={`button delete-message ${
                          m.isPendingMessage ? 'pending' : ''
                        }`}
                        onClick={() => {
                          if (m.isPendingMessage) {
                            this.setState({
                              body: m.translation || m.body,
                              imageUrl: m.imageUrl,
                              translationPreview: m.translation ? m.body : null,
                              pendingMessages: _.omit(pendingMessages, [m.id]),
                            });
                          } else {
                            if (
                              !window.confirm(
                                'This message has been sent already. Are you sure you want to delete?'
                              )
                            )
                              return;
                            deleteMessage(m.id);
                          }
                        }}
                      >
                        <i
                          className={`icon ${
                            m.isPendingMessage
                              ? 'bepro-close'
                              : 'bepro-trash-can'
                          }`}
                        />
                      </button>
                    ) : (
                      ''
                    )}
                  </div>,
                  m.type !== 'admin' &&
                    m.type !== _.get(orderedMessages, `[${i + 1}].type`) && (
                      <div
                        key={m.id + '-name'}
                        className="message-user-name"
                        style={{
                          alignSelf:
                            m.type === 'admin' ? 'flex-end' : 'flex-start',
                        }}
                      >
                        {chatroom?.name}
                      </div>
                    ),
                ])}
              </div>
            ))}
          </Column>
          <form
            className={'chat-room-footer'}
            onSubmit={this._onSubmit}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                this._onSubmit(e);
              }
            }}
          >
            <label className={'btn-icon'}>
              <i className="icon bepro-image" />
              <input
                type="file"
                accept="image/*"
                onChange={this._onFileSelect}
              />
            </label>

            {translationEnabled && translationPreview ? (
              <div className="translation-preview">
                <div className="translation-preview-header">
                  <span>Sending in {languageFlag(selectedTargetLanguage)}</span>
                  <button
                    type="button"
                    className="btn-dismiss-preview"
                    onClick={this._onDismissPreview}
                  >
                    <i className="icon bepro-close" />
                  </button>
                </div>
                <div className="translation-preview-body">
                  {translationPreview}
                </div>
              </div>
            ) : (
              <textarea
                autoFocus
                value={body}
                onPaste={this._onPaste}
                placeholder={'Write something to the customer 😊'}
                onChange={(e) => this.setState({ body: e.target.value })}
              />
            )}
            {translationEnabled && (
              <div className="translate-controls">
                <select
                  className="language-select"
                  value={selectedTargetLanguage}
                  onChange={(e) => this._onSelectTargetLanguage(e.target.value)}
                >
                  {_.map(TARGET_LANGUAGES, ({ code, flag }) => (
                    <option key={code} value={code}>
                      {flag}
                    </option>
                  ))}
                </select>
                {!translationPreview && (
                  <button
                    type="button"
                    className="btn-translate"
                    disabled={!canTranslatePreview}
                    onClick={this._onTranslatePreview}
                  >
                    {translatingPreview ? 'Translating…' : 'Translate'}
                  </button>
                )}
              </div>
            )}
            <button className={'btn-icon submit'} type="submit">
              <i className="icon bepro-send" />
            </button>

            {!!imageUrl && (
              <div className="image-wrapper">
                <img src={imageUrl} alt="" />
                <button
                  className={'btn-remove-image'}
                  onClick={(e) => this.setState({ imageUrl: null })}
                >
                  <i className="icon bepro-close" />
                </button>
              </div>
            )}
          </form>

          {uploadingImage && (
            <div className="loading">
              <ReactLoading type="bubbles" />
            </div>
          )}
        </Column>
      </ChatRoomStyle>
    );
  }
}

const getListeners = ({ ownProps }) => {
  const { userId } = ownProps;

  return {
    chatrooms: {
      where: ['userId', '==', userId],
    },
    messages: {
      where: ['userId', '==', userId],
    },
  };
};

const dataToProps = ({ data, firebase, ownProps }) => {
  const { userId } = ownProps;
  return {
    chatroom: _.first(_.values(data.chatrooms)),
    messages: _.pickBy(data.messages, { userId }),
    sendMessage: (
      body,
      {
        language = null,
        imageUrl = null,
        attachments = null,
        channel = null,
        translation = null,
      } = {}
    ) => {
      const payload = {
        userId,
        body,
        imageUrl,
        language,
        attachments,
        channel,
        type: 'admin', // 'user' or 'admin'
        adminUserEmail: firebase.auth().currentUser.email,
        created: firebase.firestore.FieldValue.serverTimestamp(),
      };
      if (translation) payload.translation = translation;
      return firebase.firestore().collection('messages').add(payload);
    },
    translateText: async (text, targetLanguage, direction) => {
      const callable = firebase.functions().httpsCallable('translate');
      const { data } = await callable({ text, targetLanguage, direction });
      return data;
    },
    saveTranslation: (messageId, translation, sourceLanguage) => {
      const payload = { translation };
      if (sourceLanguage) payload.sourceLanguage = sourceLanguage;
      return firebase
        .firestore()
        .collection('messages')
        .doc(messageId)
        .update(payload);
    },
    uploadImage: async (imageFile) => {
      const storageRef = firebase.storage().ref();
      const imageRef = storageRef.child(
        `images/${Date.now()}_${imageFile.name}`
      );
      const res = await imageRef.put(imageFile);

      return await res.ref.getDownloadURL();
    },
    deleteMessage: (messageId) => {
      firebase.firestore().collection('messages').doc(messageId).delete();
    },
    toggleSubscription: (isSubscribed) => {
      const currentUserEmail = firebase.auth().currentUser.email;
      const chatroomRef = firebase.firestore().doc(`chatrooms/${userId}`);
    
      return chatroomRef.update({
        subscribers: isSubscribed 
          ? firebase.firestore.FieldValue.arrayUnion(currentUserEmail)
          : firebase.firestore.FieldValue.arrayRemove(currentUserEmail)
      });
    }
  };
};

export const ConnectedChatRoom = connectFirebase(
  getListeners,
  dataToProps
)(ChatRoom);

export const ChatRoomRouteComponent = ({
  match: {
    params: { userId },
  },
}) => {
  return <ConnectedChatRoom userId={userId} />;
};
